import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  JSX,
  ReactNode,
} from 'react';

/**
 * The shared primitives. They carry `cib-` classes from `tokens.css` and no
 * styling library, so `@croissants/ui` depends on React and nothing else
 * (tech-stack section 4) and an app can still restyle any of them.
 *
 * Every string these components render is passed in already translated: no copy
 * is hardcoded in a component (PRD NFR-006).
 */

export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

/**
 * `success` is `danger`'s opposite number and exists for the same reason: an
 * irreversible YES that has to be told apart from the button beside it before
 * it is read. The counter tablet is the case - Accepter and Refuser sit on the
 * same card, both change the order, and brown-on-brown made them one shape.
 * It is not a decoration for an ordinary primary action; a screen with a green
 * button and no red one beside it wants `primary`.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'neutral' | 'danger' | 'success';
/**
 * Five steps, chosen by what the button SAYS, not by where it sits:
 *
 *  - `xs` a glyph or two characters - "+", "-", a page number, a count;
 *  - `sm` a row action inside a table or a list;
 *  - `md` the default - an ordinary action in a form or a toolbar;
 *  - `lg` the one primary action on a page;
 *  - `xl` a funnel's hero, alone on its row.
 *
 * There is no surface-specific step. The bakery tablet's 60px target
 * (PRD BAK-024) is the tokens retuned at its root - `--cib-control` and
 * friends - so `md` there IS 60px and every step moves with it; a `size`
 * naming a device was how a "+" and "Confirm and pay" ended up in one box.
 */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * A plain-text label is wrapped so it can truncate inside the button's cap
 * (`--cib-button-max-*`); a label built from elements is left exactly as it was
 * passed, because truncating one of those truncates whatever it contains.
 */
function buttonLabel(children: ReactNode): ReactNode {
  return typeof children === 'string' || typeof children === 'number' ? (
    <span className="cib-button__text">{children}</span>
  ) : (
    children
  );
}

/** The words in full, for the label the cap cut short. */
function labelTitle(children: ReactNode, title: string | undefined): string | undefined {
  if (title !== undefined) return title;
  return typeof children === 'string' ? children : undefined;
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Announced to assistive technology while `loading` is true. */
  loadingLabel?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * There is deliberately no `block` prop and no full-width variant: a button is
 * as wide as its label plus its padding, everywhere (see the design rule above
 * `.cib-button` in tokens.css). A layout that wants a button somewhere places
 * the button; it never inflates it.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel,
  className,
  children,
  disabled,
  title,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      {...rest}
      type={type}
      title={labelTitle(children, title)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'cib-button',
        `cib-button--${variant}`,
        size !== 'md' && `cib-button--${size}`,
        className,
      )}
    >
      {/*
       * The busy state is laid OVER the label, never inserted beside it.
       *
       * Inserting a spinner made the button wider for as long as the request was
       * in flight. In the bakery counter's top bar - seven controls on one
       * wrapping row - that extra 24px pushed the nav onto a second line and
       * pulled it back a moment later, so the whole header appeared to flicker
       * on every tap: every counter action refetches the queue, and the refresh
       * control is bound to that same fetch. The label keeps its box here
       * (`visibility`, not `display`), so the button cannot change size and
       * nothing around it can reflow.
       */}
      {loading ? (
        <span className="cib-button__busy">
          <Spinner size={16} label={loadingLabel} />
        </span>
      ) : null}
      <span className={cx('cib-button__label', loading && 'cib-button__label--busy')}>
        {buttonLabel(children)}
      </span>
    </button>
  );
}

/**
 * The one save control (PRD BAK-010, and the same rule on every portal): a Save
 * button is ARMED - butter, pressable, reading its save label - exactly while
 * the form holds an edit the platform has not stored, and quiet otherwise.
 *
 * This is a component rather than a convention because the convention was
 * reimplemented per screen and mostly not at all: the bakery tablet had it, the
 * other three portals shipped Saves that looked identical whether or not there
 * was anything to save. Every form's Save renders this, so a form cannot get the
 * state wrong without also losing its button.
 *
 * `dirty` is computed by the caller against what the server last answered - the
 * loaded record for an edit form, the initial defaults for a create form - so a
 * successful save disarms the button by itself once the fresh record arrives.
 */
export interface SaveButtonProps extends Omit<ButtonProps, 'children' | 'variant'> {
  /** True while the form differs from what is stored. Arms the button. */
  dirty: boolean;
  /** Already translated, e.g. "Enregistrer". */
  saveLabel: ReactNode;
  /**
   * Shown, disabled, while the form matches what is stored - the saved label is
   * a state, not a message. Omit on a create form, where nothing is stored yet
   * and the save label is true in both states. Already translated.
   */
  savedLabel?: ReactNode;
  /** A validation problem that must keep an armed button unpressable. */
  blocked?: boolean;
}

export function SaveButton({
  dirty,
  saveLabel,
  savedLabel,
  blocked = false,
  disabled = false,
  className,
  ...rest
}: SaveButtonProps): JSX.Element {
  return (
    <Button
      {...rest}
      variant={dirty ? 'primary' : 'secondary'}
      className={cx(dirty && 'cib-button--dirty', className)}
      disabled={disabled || blocked || !dirty}
    >
      {/*
       * Both labels are always in the box, stacked on one grid cell, and the
       * state only toggles which one is visible - the same keep-the-box rule as
       * the busy overlay above. Swapping the text node instead made the button
       * change width on every edit ("Enregistrer" and "Enregistré" measure
       * differently), which walked everything beside it left and right as the
       * form armed and disarmed.
       */}
      {savedLabel === undefined ? (
        saveLabel
      ) : (
        <span className="cib-button__swap">
          <span className={cx(!dirty && 'cib-button__swap--off')}>{saveLabel}</span>
          <span className={cx(dirty && 'cib-button__swap--off')}>{savedLabel}</span>
        </span>
      )}
    </Button>
  );
}

export interface LinkButtonProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children?: ReactNode;
}

export function LinkButton({
  variant = 'primary',
  size = 'md',
  className,
  children,
  title,
  ...rest
}: LinkButtonProps): JSX.Element {
  return (
    <a
      {...rest}
      title={labelTitle(children, title)}
      className={cx(
        'cib-button',
        `cib-button--${variant}`,
        size !== 'md' && `cib-button--${size}`,
        className,
      )}
    >
      <span className="cib-button__label">{buttonLabel(children)}</span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  flat?: boolean;
  interactive?: boolean;
  children?: ReactNode;
}

export function Card({
  title,
  subtitle,
  actions,
  flat = false,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps): JSX.Element {
  const hasHeader = title !== undefined || subtitle !== undefined || actions !== undefined;
  return (
    <div
      {...rest}
      className={cx(
        'cib-card',
        flat && 'cib-card--flat',
        interactive && 'cib-card--interactive',
        className,
      )}
      {...(interactive ? { tabIndex: 0, role: 'button' } : {})}
    >
      {hasHeader ? (
        <div className="cib-card__header">
          <div>
            {title !== undefined ? <h3 className="cib-card__title">{title}</h3> : null}
            {subtitle !== undefined ? <p className="cib-card__subtitle">{subtitle}</p> : null}
          </div>
          {actions !== undefined ? <div className="cib-card__actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record state - the one place a record's standing is read and changed
// ---------------------------------------------------------------------------

export interface RecordStateProps {
  /** The record's own standing: on is live, off is suspended. */
  active: boolean;
  /** Accessible name for the switch. Already translated (PRD NFR-006). */
  label: string;
  /**
   * Called with what the operator asked for, NOT with a new local value. A
   * caller drives `active` from the server's answer and opens its confirmation
   * here, so a cancelled suspension leaves the switch exactly where it was
   * without any state of its own to reset.
   */
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** The state pill(s) that read the same fact back. Already translated. */
  children?: ReactNode;
}

/**
 * A record's state, drawn once for the whole platform: the switch that changes
 * it beside the pill that states it.
 *
 * It goes in the `actions` slot of a detail screen's FIRST card, so the answer
 * to "is this thing live" is in the same corner of every record on the
 * platform. It used to be somewhere different on each: buried in a definition
 * list on one screen, in a "State" card below the fold on two more, and in a
 * floating paragraph above the form on a fourth - which is what made the
 * question worth hunting for rather than worth glancing at.
 */
export function RecordState({
  active,
  label,
  onChange,
  disabled = false,
  children,
}: RecordStateProps): JSX.Element {
  return (
    <div className="cib-recordstate">
      <input
        className="cib-switch"
        type="checkbox"
        role="switch"
        aria-label={label}
        aria-checked={active}
        checked={active}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product photograph
// ---------------------------------------------------------------------------

export interface ProductPhotoProps {
  /** The photograph, or null/undefined when the product has none authored. */
  src?: string | null;
  /** Empty when a name beside the photo already labels it. */
  alt?: string;
  /** Drawn inside the empty square when there is no photograph. */
  placeholder?: ReactNode;
  /**
   * `lazy` for a picture below the fold, `eager` for one the reader is looking
   * at. A shelf's first row is eager: a lazy picture above the fold is fetched
   * last and at low priority, which is the wrong order for the tiles a guest
   * sees first. Decoding is always off the main thread (`decoding="async"`), so
   * a row of pictures arriving together cannot stall the scroll.
   */
  loading?: 'eager' | 'lazy';
  /** The surface's own class - its WIDTH, and anything else local to it. */
  className?: string;
  /**
   * `square` (the default) is the shape every shelf on the platform is read
   * down. `flush` is the ONE documented exception: a picture that IS the left
   * edge of the row it leads, filling the row's height with no inset above,
   * below or beside it, and squaring off its corners into the card's own. It
   * exists here rather than in an app stylesheet because the height and the
   * corner belong to this class, and an app that set them would be forking the
   * shape - which is what `check-control-metrics.mjs` fails the build over.
   */
  variant?: 'square' | 'flush';
  /**
   * Makes the square a button that opens the picture full size (`PhotoViewer`).
   * Ignored when there is no photograph, because an empty square has nothing to
   * open and a button that does nothing is worse than no button.
   */
  onZoom?: () => void;
  /** Accessible name for that button. Already translated; required with `onZoom`. */
  zoomLabel?: string;
}

/**
 * A product's photograph, square, everywhere on the platform.
 *
 * There is deliberately no shape prop and no size prop. A product is shown in a
 * square with its text BELOW it - the guest menu and item sheet, the bakery
 * tablet's stock rows, the internal catalogue and its image editor, the partner
 * product and stock lists - and photographs arrive in whatever ratio the bakery
 * shot them in, mostly portrait. The square is what makes a shelf of products
 * scan as a shelf; letting each surface pick its own box is what produced a 4:3
 * one on the guest item sheet, which magnified a portrait photo into a
 * horizontal band across the middle of a pain au chocolat.
 *
 * The caller owns only the box's WIDTH (a row thumb, a column, the full width of
 * a sheet); height, crop, corners and the empty ground come from `.cib-photo`,
 * so a surface cannot fork the shape by accident. Missing photographs still
 * occupy their square, so a row of names cannot lift out of line.
 */
export function ProductPhoto({
  src,
  alt = '',
  placeholder,
  loading,
  className,
  variant = 'square',
  onZoom,
  zoomLabel,
}: ProductPhotoProps): JSX.Element {
  const flush = variant === 'flush' && 'cib-photo--flush';
  if (src === undefined || src === null || src === '') {
    return (
      <div className={cx('cib-photo', 'cib-photo--empty', flush, className)} aria-hidden="true">
        {placeholder}
      </div>
    );
  }
  /*
   * The surface's class moves to the BUTTON when there is one, because that
   * class is the box's width and the width has to be on the outermost element
   * for the square inside it to take its height from. The picture keeps
   * `.cib-photo` and nothing else.
   */
  return onZoom === undefined ? (
    <img
      className={cx('cib-photo', flush, className)}
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
    />
  ) : (
    <button
      type="button"
      className={cx('cib-zoom', variant === 'flush' && 'cib-zoom--flush', className)}
      aria-label={zoomLabel}
      onClick={onZoom}
    >
      <img
        className={cx('cib-photo', flush)}
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// A partner's brand mark
// ---------------------------------------------------------------------------

export interface BrandMarkProps {
  /** The mark, or null/undefined when the brand has none uploaded. */
  src?: string | null;
  /** Empty when a name beside the mark already labels it. */
  alt?: string;
  /** Drawn inside the empty square when there is no mark. */
  placeholder?: ReactNode;
  loading?: 'eager' | 'lazy';
  /** The surface's own class - its WIDTH, and anything else local to it. */
  className?: string;
  /** Opens the mark full size, as on `ProductPhoto`. Ignored when there is none. */
  onZoom?: () => void;
  /** Accessible name for that button. Already translated; required with `onZoom`. */
  zoomLabel?: string;
}

/**
 * A PARTNER's mark - a bakery's or a mode B shop's - contained in its box and
 * never cropped to it. This platform's own mark is `LogoStamp` / `Wordmark` in
 * `logo.tsx` and has nothing to do with this component.
 *
 * It exists because `ProductPhoto` was used for one, and a product photo crops:
 * the mode B shop editor rendered Leonie's wordmark as `EONI`, having thrown
 * away both ends of the name to fill a square. The distinction is not cosmetic -
 * a photograph loses nothing identifying when its edges go, and a wordmark loses
 * the only thing it carries.
 *
 * Same contract as `ProductPhoto` otherwise: the caller owns the WIDTH, and the
 * square, the fit, the padding, the corners and the ground come from
 * `.cib-brandmark`, so no surface can fork the shape by accident.
 */
export function BrandMark({
  src,
  alt = '',
  placeholder,
  loading,
  className,
  onZoom,
  zoomLabel,
}: BrandMarkProps): JSX.Element {
  if (src === undefined || src === null || src === '') {
    return (
      <div className={cx('cib-brandmark', 'cib-brandmark--empty', className)} aria-hidden="true">
        {placeholder}
      </div>
    );
  }
  // Same reasoning as `ProductPhoto`: the caller's class is the box's width, so
  // it moves to the button and the mark keeps only its own shape class.
  return onZoom === undefined ? (
    <img className={cx('cib-brandmark', className)} src={src} alt={alt} loading={loading} />
  ) : (
    <button
      type="button"
      className={cx('cib-zoom', className)}
      aria-label={zoomLabel}
      onClick={onZoom}
    >
      <img className="cib-brandmark" src={src} alt={alt} loading={loading} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

/**
 * `neutral` is the brand's deep cream; `grey` is a true grey for a status that
 * is inactive but not a problem (a shop closed by its own calendar).
 */
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'grey' | 'orange';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /**
   * Draws the status dot. Colour alone never conveys state (PRD NFR-005,
   * BAK-026), so the badge's own text is always the primary signal.
   */
  dot?: boolean;
  children?: ReactNode;
}

export function Badge({
  tone = 'neutral',
  dot = true,
  className,
  children,
  ...rest
}: BadgeProps): JSX.Element {
  return (
    <span {...rest} className={cx('cib-badge', `cib-badge--${tone}`, className)}>
      {dot ? <span className="cib-badge__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------

export interface SpinnerProps {
  size?: number;
  /** Visually hidden text. Omit inside a container that already announces busy. */
  label?: string;
  className?: string;
}

export function Spinner({ size = 20, label, className }: SpinnerProps): JSX.Element {
  return (
    <>
      <span
        className={cx('cib-spinner', className)}
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
      {label ? (
        <span className="cib-visually-hidden" role="status">
          {label}
        </span>
      ) : null}
    </>
  );
}
